/**
 * PanelAccesoWifi: presentacional puro de la pantalla Acceso WiFi (F1).
 *
 * Sin fetch ni estado de servidor: recibe todo por props para poder validarse
 * en el preview dev-only `/dev/acceso-wifi` antes de que exista backend.
 * En F5 se monta en `/tesoreria/acceso-wifi` con datos reales.
 *
 * Doctrina (ver `docs/dev/plan-ewelink.md`):
 * - Ejecución ASISTIDA: nada se activa ni se revoca solo, salvo morosidad.
 *   La selección múltiple no la contradice: sigue apretando un humano, solo
 *   que sobre N filas en vez de una (con 47 accesos, de a uno es inviable).
 * - EL PORTAL MANDA: todo acceso que exista en eWeLink sin su ticket en el
 *   portal se elimina. Da igual si la persona es conocida — si necesita
 *   acceso, lo solicita por el flujo normal.
 * - `puedeOperar` separa autorizar de operar: Comisión ve todo, Tesorería
 *   además ejecuta.
 *
 * La selección es POR SECCIÓN, no global: cada lista tiene su propia acción
 * (activar / dar de baja / eliminar de eWeLink) y una selección compartida
 * dejaría ambiguo qué botón aplica a qué fila.
 */
import { useMemo, useState } from 'react';
import { Button } from '../ui/Button';
import { CirculoWifi, type EstadoWifi } from '../ui/CirculoWifi';
import { EmptyState } from '../ui/EmptyState';

export interface AccesoWifiFila {
  acceso_id: string;
  lote_identificador: string;
  residente_nombre: string;
  parentesco: string | null;
  email: string;
  estado: EstadoWifi;
  /** Alta del acceso en eWeLink: el `shareTime` que devuelve `shareTo`.
   *  Es cuándo el vecino quedó habilitado, no cuándo lo miramos. */
  alta_ewelink_at: string | null;
  /** Detalle del último fallo, si `estado === 'error'`. */
  error: string | null;
  /** Etiqueta con la que figura en la app de eWeLink (`casa-iniciales`). */
  comment?: string | null;
  /** False si los canales no comparten la misma etiqueta. */
  comment_consistente?: boolean;
  comments_en_ewelink?: string[];
  /** Correo del residente HOY. Si difiere de `email` —el compartido en
   *  eWeLink— el acceso apunta a un correo que ya no es suyo. */
  email_actual?: string | null;
}

export interface HuerfanoEWeLink {
  email: string;
  /** Necesario para revocar: el DELETE de eWeLink es por apikey. */
  apikey: string;
  comment: string | null;
  compartido_desde: string | null;
  /** Si el correo pertenece a alguien del padrón, la casa donde figura.
   *  Sirve para avisarle antes de cortarle, no para decidir si se corta. */
  lote_identificador: string | null;
  nombre_padron: string | null;
}

/** Ocupación de un dispositivo administrado. eWeLink admite 199 usuarios
 *  compartidos por device; los virtuales existen para absorber el
 *  desborde cuando un físico se llena. */
export interface OcupacionDispositivo {
  deviceid: string;
  nombre: string;
  es_virtual: boolean;
  usados: number;
  limite: number;
  /** Agrupa el físico con su virtual de desborde. */
  canal: string;
}

/** Correo compartido en algunos canales, no en todos (2026-10-06). */
export interface AccesoIncompleto {
  email: string;
  canales_presentes: string[];
  canales_faltantes: string[];
  /** Solo si se puede completar (ticket vigente, sin suspensión ni correo
   *  cambiado). */
  acceso_id: string | null;
  lote_identificador: string | null;
  nombre: string | null;
  /** Por qué no se ofrece Completar (null = sí se ofrece). */
  motivo_sin_accion: 'sin_ticket' | 'correo_cambiado' | 'suspendido' | null;
}

const MOTIVO_SIN_ACCION: Record<
  NonNullable<AccesoIncompleto['motivo_sin_accion']>,
  string
> = {
  sin_ticket: 'Sin ticket — sin acción',
  correo_cambiado: 'Cambió el correo — usa Reprogramar',
  suspendido: 'Suspendido — no se completa',
};

export interface DatosAccesoWifi {
  porActivar: AccesoWifiFila[];
  porDarDeBaja: AccesoWifiFila[];
  activos: AccesoWifiFila[];
  /** En eWeLink pero sin ticket en el portal. */
  huerfanos: HuerfanoEWeLink[];
  /** Compartidos en eWeLink con un correo que ya no es del residente. No
   *  aparecían en ninguna lista: el cruce usa el snapshot, que sigue
   *  coincidiendo con eWeLink, así que la pantalla decía que estaba todo bien
   *  mientras el correo viejo abría el portón y el nuevo no tenía acceso. */
  correoCambiado: AccesoWifiFila[];
  /** En el portal pero sin acceso real en eWeLink. */
  sinAccesoReal: AccesoWifiFila[];
  /** En algunos canales, no en todos: entra por unas puertas y no por otras. */
  incompletos: AccesoIncompleto[];
  /** canal → nombre del dispositivo físico (lo manda el backend). */
  nombresCanal: Record<string, string>;
  conexion: { ok: boolean; mensaje: string; consultado_at: string | null };
  /** Solo dispositivos marcados como "Administrado" en la configuración. */
  ocupacion: OcupacionDispositivo[];
}

type Tab = 'pendientes' | 'activos' | 'conciliacion';

/** Selección de filas de UNA sección. */
function useSeleccion(claves: string[]) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  // Si la lista cambia (refresco), descarta lo que ya no existe.
  const vigentes = useMemo(
    () => new Set([...sel].filter((k) => claves.includes(k))),
    [sel, claves],
  );
  return {
    sel: vigentes,
    marcada: (k: string) => vigentes.has(k),
    toggle: (k: string) =>
      setSel((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        return next;
      }),
    todas: claves.length > 0 && claves.every((k) => vigentes.has(k)),
    toggleTodas: (marcar: boolean) => setSel(marcar ? new Set(claves) : new Set()),
    limpiar: () => setSel(new Set()),
  };
}

/** Encabezado de sección con su acción masiva. La acción vive acá y no en una
 *  barra global para que nunca sea ambiguo a qué filas aplica. */
/** Etiqueta de eWeLink con un punto de consistencia al lado.
 *
 *  Verde: los canales comparten la misma etiqueta.
 *  Rojo: NO coinciden. eWeLink guarda el comment POR DISPOSITIVO, así que
 *  editarlo a mano en un canal no lo cambia en los otros — un vecino puede
 *  quedar con dos etiquetas y nadie enterarse. Caso real casa 6874
 *  (2026-09-02): tres canales decían `6974-HR` y uno `6874-HR`.
 *
 *  Lo que crea el sistema siempre es consistente: `compartir` manda un solo
 *  comment para los cuatro. El rojo señala edición manual.
 */
function EtiquetaEwelink({ fila }: { fila: AccesoWifiFila }) {
  if (!fila.comment && !(fila.comments_en_ewelink ?? []).length) {
    return <span className="text-xs text-text-muted">—</span>;
  }
  const ok = fila.comment_consistente !== false;
  const variantes = fila.comments_en_ewelink ?? [];
  return (
    <span
      className="inline-flex items-center gap-1.5"
      title={
        ok
          ? 'La etiqueta es la misma en todos los canales.'
          : `Los canales no coinciden: ${variantes.join(' / ')}. eWeLink guarda la etiqueta por dispositivo; hay que corregirla en cada uno.`
      }
    >
      <span
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${
          ok ? 'bg-state-success' : 'bg-state-danger'
        }`}
        role="img"
        aria-label={ok ? 'Etiqueta consistente' : 'Etiquetas distintas entre canales'}
      />
      <span className="font-mono text-xs text-text-secondary">
        {fila.comment ?? variantes[0] ?? '—'}
      </span>
      {!ok && variantes.length > 1 && (
        <span className="text-[10px] text-state-danger">
          +{variantes.length - 1}
        </span>
      )}
    </span>
  );
}

function Seccion({
  titulo,
  descripcion,
  cantidad,
  seleccionadas,
  accion,
  onAccionMasiva,
  onLimpiar,
  puedeOperar,
  peligro = false,
  children,
}: {
  titulo: string;
  descripcion: string;
  cantidad: number;
  seleccionadas?: number;
  accion?: string;
  onAccionMasiva?: () => void;
  onLimpiar?: () => void;
  puedeOperar: boolean;
  peligro?: boolean;
  children: React.ReactNode;
}) {
  if (cantidad === 0) return null;
  const hayMasiva =
    puedeOperar && accion && onAccionMasiva && (seleccionadas ?? 0) > 0;
  return (
    <section className="bg-bg-elevated rounded-lg shadow overflow-hidden">
      <div className="px-4 pt-4 pb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-wide text-text-primary">
            {titulo}{' '}
            <span className="text-text-secondary font-normal normal-case">
              ({cantidad})
            </span>
          </h3>
          <p className="text-xs text-text-secondary mt-0.5">{descripcion}</p>
        </div>
        {hayMasiva && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onLimpiar}>
              Limpiar
            </Button>
            <Button
              variant={peligro ? 'danger' : 'primary'}
              size="sm"
              onClick={onAccionMasiva}
            >
              {accion} {seleccionadas} seleccionado
              {seleccionadas === 1 ? '' : 's'}
            </Button>
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

function Tabla({
  filas,
  accion,
  onEjecutar,
  filaOperable = () => true,
  puedeOperar,
  mostrarAlta = false,
  mostrarCorreoNuevo = false,
  seleccion,
  enCurso = null,
  ejecutando = false,
  gerundio = 'Procesando…',
}: {
  filas: AccesoWifiFila[];
  accion?: string;
  onEjecutar?: (f: AccesoWifiFila) => void;
  /** Filtra qué filas llevan botón y casilla (por defecto, todas). */
  filaOperable?: (f: AccesoWifiFila) => boolean;
  puedeOperar: boolean;
  mostrarAlta?: boolean;
  /** Agrega la columna con el correo ACTUAL del residente. Solo tiene sentido
   *  cuando difiere del compartido — si no, serían dos columnas iguales. */
  mostrarCorreoNuevo?: boolean;
  seleccion?: ReturnType<typeof useSeleccion>;
  enCurso?: string | null;
  ejecutando?: boolean;
  /** Texto mientras la fila se procesa: "Activando…", "Dando de baja…". */
  gerundio?: string;
}) {
  const conBoton = Boolean(accion && puedeOperar && onEjecutar);
  const conCheck = Boolean(seleccion && puedeOperar && onEjecutar);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-text-muted border-y border-line">
            {conCheck && (
              <th className="pl-4 pr-1 py-2 w-8">
                <input
                  type="checkbox"
                  aria-label="Seleccionar todos"
                  checked={seleccion!.todas}
                  onChange={(e) => seleccion!.toggleTodas(e.target.checked)}
                />
              </th>
            )}
            <th className="px-4 py-2 font-medium">Casa</th>
            <th className="px-3 py-2 font-medium">Residente</th>
            <th className="px-3 py-2 font-medium">
              {mostrarCorreoNuevo ? 'Correo en eWeLink (viejo)' : 'Correo eWeLink'}
            </th>
            {mostrarCorreoNuevo && (
              <th className="px-3 py-2 font-medium">Correo actual (nuevo)</th>
            )}
            <th className="px-3 py-2 font-medium">Estado</th>
            {mostrarAlta && (
              <>
                <th className="px-3 py-2 font-medium">Etiqueta</th>
                <th className="px-3 py-2 font-medium">Alta en eWeLink</th>
              </>
            )}
            {conBoton && <th className="px-4 py-2 font-medium text-right" />}
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr
              key={f.acceso_id}
              className="border-b border-line/60 last:border-0 hover:bg-bg-page/40"
            >
              {conCheck && (
                <td className="pl-4 pr-1 py-2">
                  {filaOperable(f) && (
                    <input
                      type="checkbox"
                      aria-label={`Seleccionar ${f.residente_nombre} de casa ${f.lote_identificador}`}
                      checked={seleccion!.marcada(f.acceso_id)}
                      onChange={() => seleccion!.toggle(f.acceso_id)}
                    />
                  )}
                </td>
              )}
              <td className="px-4 py-2 font-semibold text-text-primary whitespace-nowrap">
                {f.lote_identificador}
              </td>
              <td className="px-3 py-2 text-text-primary">
                {f.residente_nombre}
                {f.parentesco && (
                  <span className="text-text-muted"> · {f.parentesco}</span>
                )}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-text-secondary break-all">
                {f.email}
              </td>
              {mostrarCorreoNuevo && (
                <td className="px-3 py-2 font-mono text-xs break-all">
                  {f.email_actual ? (
                    <span className="text-orange">{f.email_actual}</span>
                  ) : (
                    // Sin correo actual no hay nada que compartir: la
                    // reprogramación fallaría en la segunda mitad.
                    <span className="text-state-danger">sin correo</span>
                  )}
                </td>
              )}
              <td className="px-3 py-2 whitespace-nowrap">
                <span className="inline-flex items-center gap-1.5">
                  <CirculoWifi estado={f.estado} />
                  <span className="text-xs text-text-secondary">
                    {f.estado === 'error' && f.error ? f.error : null}
                  </span>
                </span>
              </td>
              {mostrarAlta && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <EtiquetaEwelink fila={f} />
                  </td>
                  <td className="px-3 py-2 text-xs text-text-muted whitespace-nowrap">
                    {f.alta_ewelink_at ?? '—'}
                  </td>
                </>
              )}
              {conBoton && (
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {filaOperable(f) && (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={ejecutando}
                      onClick={() => onEjecutar?.(f)}
                    >
                      {enCurso === f.acceso_id ? gerundio : accion}
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Ocupación de cupos, en una línea.
 *
 *  Lo que se cuenta son ACCESOS ocupando cupos de UN canal, no la suma de
 *  los dispositivos: cada acceso se comparte en los cuatro canales a la vez
 *  (portón/puerta × entrada/salida), así que los físicos no son capacidades
 *  independientes — son los mismos accesos replicados. La capacidad de un
 *  canal es el límite del físico + el de su virtual de desborde (hoy
 *  199 + 199 = 398 cupos).
 *
 *  Un acceso equivale a una persona solo porque el correo es único
 *  (convención #18): antes de esa restricción, tres vecinos de la misma casa
 *  compartían un correo y por tanto un solo cupo.
 *
 *  Si los físicos no marcan lo mismo, hay descuadre entre canales y eso se
 *  avisa: significa que alguien quedó habilitado en una puerta y no en otra.
 */
function OcupacionResumen({ items }: { items: OcupacionDispositivo[] }) {
  if (items.length === 0) return null;

  const fisicos = items.filter((d) => !d.es_virtual);
  const virtuales = items.filter((d) => d.es_virtual);
  if (fisicos.length === 0) return null;

  const limite = fisicos[0].limite;
  const capasPorCanal = 1 + Math.floor(virtuales.length / fisicos.length);
  const capacidad = limite * capasPorCanal;

  const usadosFisico = Math.max(...fisicos.map((d) => d.usados));
  const usadosVirtual = virtuales.length
    ? Math.max(...virtuales.map((d) => d.usados))
    : 0;
  // Cupos ocupados en UN canal (físico + su virtual de desborde).
  const ocupados = usadosFisico + usadosVirtual;
  const pct = capacidad === 0 ? 0 : Math.round((ocupados / capacidad) * 100);

  // Total por CANAL (físico + su virtual): comparar dispositivos sueltos
  // marcaría descuadre apenas un canal empiece a desbordar al virtual.
  const porCanal = new Map<string, number>();
  for (const d of items) {
    const clave = d.canal || d.deviceid;
    porCanal.set(clave, (porCanal.get(clave) ?? 0) + d.usados);
  }
  const totalesCanal = [...porCanal.values()];
  const desalineados = new Set(totalesCanal).size > 1;

  const { color, borde, texto } =
    pct >= 90
      ? { color: 'bg-state-danger', borde: 'border-state-danger', texto: 'Complicado' }
      : pct >= 70
        ? { color: 'bg-state-warning', borde: 'border-state-warning', texto: 'Ajustado' }
        : { color: 'bg-state-success', borde: 'border-state-success', texto: 'Holgado' };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />
        <span className="font-medium text-text-primary">{texto}</span>
      </span>
      <div
        className={`h-2 w-24 overflow-hidden rounded-full border ${borde} bg-bg-page`}
      >
        <div
          className={`h-full ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span
        className="tabular-nums text-text-secondary"
        title={`Cupos de un canal: ${limite} en el dispositivo físico + ${limite} en su virtual de desborde. Cada acceso ocupa un cupo en cada uno de los ${fisicos.length} canales, así que los físicos no son capacidades independientes: son los mismos accesos replicados.`}
      >
        {ocupados} de {capacidad} cupos ({pct}%)
      </span>
      {usadosVirtual > 0 && (
        <span className="text-text-muted">· {usadosVirtual} en desborde</span>
      )}
      {desalineados && (
        <span className="text-state-danger">
          ⚠ Los canales no coinciden ({totalesCanal.join(' / ')}) — detalle
          en Conciliación → Accesos incompletos
        </span>
      )}
    </div>
  );
}

export function PanelAccesoWifi({
  datos,
  puedeOperar,
  onActivar,
  onDarDeBaja,
  onEliminarDeEwelink,
  onReprogramarCorreo,
  onCompletar,
  onVolverACompartir,
  onRefrescar,
  enCurso = null,
  ejecutando = false,
}: {
  datos: DatosAccesoWifi;
  /** Tesorería ejecuta; Comisión solo mira. */
  puedeOperar: boolean;
  onActivar?: (filas: AccesoWifiFila[]) => void;
  onDarDeBaja?: (filas: AccesoWifiFila[]) => void;
  /** Revoca en eWeLink accesos sin ticket en el portal. */
  onEliminarDeEwelink?: (items: HuerfanoEWeLink[]) => void;
  /** Quita el correo viejo de eWeLink y comparte el nuevo, en ese orden. */
  onReprogramarCorreo?: (filas: AccesoWifiFila[]) => void;
  /** Comparte un acceso activo en los canales donde le falta. */
  onCompletar?: (item: AccesoIncompleto) => void;
  /** Comparte de nuevo un acceso que no está en ningún canal. */
  onVolverACompartir?: (filas: AccesoWifiFila[]) => void;
  onRefrescar?: () => void;
  /** `acceso_id` en curso: su botón muestra el gerundio. */
  enCurso?: string | null;
  /** Hay una operación corriendo: se bloquean los demás botones para no
   *  encolar acciones sobre una lista que está por refrescarse. */
  ejecutando?: boolean;
}) {
  const [tab, setTab] = useState<Tab>('pendientes');

  const selActivar = useSeleccion(datos.porActivar.map((f) => f.acceso_id));
  const selBaja = useSeleccion(datos.porDarDeBaja.map((f) => f.acceso_id));
  const selHuerfanos = useSeleccion(datos.huerfanos.map((h) => h.email));
  // Solo las filas que se pueden volver a compartir: si entraran las "en
  // validación", "Seleccionar todos" las marcaría sin casilla visible y el
  // botón mostraría más de lo que ejecuta.
  const selSinAcceso = useSeleccion(
    datos.sinAccesoReal
      .filter((f) => f.estado === 'acceso_perdido')
      .map((f) => f.acceso_id),
  );
  const selCorreo = useSeleccion(datos.correoCambiado.map((f) => f.acceso_id));

  const pendientes = datos.porActivar.length + datos.porDarDeBaja.length;
  const diferencias =
    datos.huerfanos.length +
    datos.sinAccesoReal.length +
    datos.correoCambiado.length +
    // Los sin ticket ya cuentan en "Sin ticket" y los de correo cambiado en
    // "Cambió el correo": sumarlos otra vez inflaría el contador.
    datos.incompletos.filter(
      (i) =>
        i.motivo_sin_accion !== 'sin_ticket' &&
        i.motivo_sin_accion !== 'correo_cambiado',
    ).length;
  const etiquetaCanal = (canal: string) => datos.nombresCanal[canal] ?? canal;

  const TABS: { id: Tab; label: string; n: number }[] = [
    { id: 'pendientes', label: 'Pendientes', n: pendientes },
    { id: 'activos', label: 'Activos', n: datos.activos.length },
    { id: 'conciliacion', label: 'Conciliación', n: diferencias },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Acceso WiFi</h2>
          <p className="text-sm text-text-secondary">
            Accesos a los portones y puertas, administrados en eWeLink.
          </p>
        </div>
        <div className="flex items-start gap-3">
          <div className="text-right">
            <span className="inline-flex items-center gap-1.5 text-xs">
              <span
                className={`inline-block h-2 w-2 rounded-full ${
                  datos.conexion.ok ? 'bg-state-success' : 'bg-state-danger'
                }`}
              />
              <span className="text-text-secondary">
                {datos.conexion.mensaje}
              </span>
            </span>
            {/* La sincronización es "cuándo miramos", no "cuándo se dio de
                alta al vecino": va acá y no en la tabla. */}
            <p className="text-[11px] text-text-muted mt-0.5">
              {datos.conexion.consultado_at
                ? `Sincronizado ${datos.conexion.consultado_at}`
                : 'Sin sincronizar'}
            </p>
          </div>
          {onRefrescar && (
            <Button variant="secondary" size="sm" onClick={onRefrescar}>
              Actualizar
            </Button>
          )}
        </div>
      </header>

      <OcupacionResumen items={datos.ocupacion} />

      {!puedeOperar && (
        <p className="text-xs text-text-muted">
          Vista de solo lectura: la activación y la baja las ejecuta Tesorería.
        </p>
      )}

      <nav className="flex gap-1 border-b border-line" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-orange text-text-primary'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {t.label}{' '}
            <span className="text-text-muted font-normal">({t.n})</span>
          </button>
        ))}
      </nav>

      {tab === 'pendientes' && (
        <div className="space-y-4">
          {pendientes === 0 && (
            <EmptyState title="No hay accesos pendientes de ejecutar." />
          )}
          <Seccion
            titulo="Por activar"
            descripcion="Autorizados por la Comisión. Todavía no existen en eWeLink: el portón aún no los reconoce."
            cantidad={datos.porActivar.length}
            seleccionadas={selActivar.sel.size}
            accion="Activar"
            onAccionMasiva={() =>
              onActivar?.(
                datos.porActivar.filter((f) => selActivar.marcada(f.acceso_id)),
              )
            }
            onLimpiar={selActivar.limpiar}
            puedeOperar={puedeOperar}
          >
            <Tabla
              filas={datos.porActivar}
              accion="Activar"
              onEjecutar={(f) => onActivar?.([f])}
              puedeOperar={puedeOperar}
              seleccion={selActivar}
              enCurso={enCurso}
              ejecutando={ejecutando}
              gerundio="Activando…"
            />
          </Seccion>
          <Seccion
            titulo="Por dar de baja"
            descripcion="El vecino los desactivó o dejó la casa. Siguen con acceso hasta que se ejecute la baja."
            cantidad={datos.porDarDeBaja.length}
            seleccionadas={selBaja.sel.size}
            accion="Dar de baja"
            onAccionMasiva={() =>
              onDarDeBaja?.(
                datos.porDarDeBaja.filter((f) => selBaja.marcada(f.acceso_id)),
              )
            }
            onLimpiar={selBaja.limpiar}
            puedeOperar={puedeOperar}
            peligro
          >
            <Tabla
              filas={datos.porDarDeBaja}
              accion="Dar de baja"
              onEjecutar={(f) => onDarDeBaja?.([f])}
              puedeOperar={puedeOperar}
              seleccion={selBaja}
              enCurso={enCurso}
              ejecutando={ejecutando}
              gerundio="Dando de baja…"
            />
          </Seccion>
        </div>
      )}

      {tab === 'activos' && (
        <section className="bg-bg-elevated rounded-lg shadow overflow-hidden">
          {datos.activos.length === 0 ? (
            <div className="p-4">
              <EmptyState title="No hay accesos activos." />
            </div>
          ) : (
            <Tabla filas={datos.activos} puedeOperar={false} mostrarAlta />
          )}
        </section>
      )}

      {tab === 'conciliacion' && (
        <div className="space-y-4">
          <p className="text-xs text-text-secondary">
            Comparación entre lo que el portal registra y lo que eWeLink tiene.
            <strong> El portal manda:</strong> todo acceso que exista en
            eWeLink sin su ticket en el portal corresponde eliminarlo.
          </p>
          {diferencias === 0 && (
            <EmptyState title="El portal y eWeLink coinciden." />
          )}
          {/* Va PRIMERO entre las diferencias: es la única de las tres que
              deja a un vecino sin su acceso mientras otro correo —que puede
              ya no ser suyo— sigue abriendo el portón. */}
          <Seccion
            titulo="Cambió el correo"
            descripcion="El residente cambió su correo y la Comisión ya lo autorizó. El correo VIEJO sigue abriendo el portón y el nuevo no tiene acceso: al reprogramar se quita el viejo y se comparte el nuevo, en ese orden."
            cantidad={datos.correoCambiado.length}
            seleccionadas={selCorreo.sel.size}
            accion="Reprogramar"
            onAccionMasiva={() =>
              onReprogramarCorreo?.(
                datos.correoCambiado.filter((f) =>
                  selCorreo.marcada(f.acceso_id),
                ),
              )
            }
            onLimpiar={selCorreo.limpiar}
            puedeOperar={puedeOperar}
          >
            <Tabla
              filas={datos.correoCambiado}
              accion="Reprogramar"
              onEjecutar={(f) => onReprogramarCorreo?.([f])}
              puedeOperar={puedeOperar}
              seleccion={selCorreo}
              enCurso={enCurso}
              ejecutando={ejecutando}
              gerundio="Reprogramando…"
              mostrarCorreoNuevo
            />
          </Seccion>
          <Seccion
            titulo="Accesos incompletos"
            descripcion="Están en algunos canales y no en todos: la persona entra por unas puertas y no por otras. Suele pasar cuando alguien quita o comparte un acceso a mano en la app de eWeLink. Con ticket del portal, Completar lo comparte solo donde falta, con la misma etiqueta. Sin ticket no se completa: el camino es solicitarlo por el portal."
            cantidad={datos.incompletos.length}
            puedeOperar={puedeOperar}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-text-muted border-y border-line">
                    <th className="px-4 py-2 font-medium">Casa</th>
                    <th className="px-3 py-2 font-medium">Persona</th>
                    <th className="px-3 py-2 font-medium">Correo</th>
                    <th className="px-3 py-2 font-medium">Tiene</th>
                    <th className="px-3 py-2 font-medium">Le falta</th>
                    <th className="px-4 py-2 font-medium text-right" />
                  </tr>
                </thead>
                <tbody>
                  {datos.incompletos.map((i) => (
                    <tr
                      key={i.email}
                      className="border-b border-line/60 last:border-0"
                    >
                      <td className="px-4 py-2 font-semibold text-text-primary whitespace-nowrap">
                        {i.lote_identificador ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-text-primary">
                        {i.nombre ?? (
                          <span className="text-text-muted">Desconocido</span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-text-secondary">
                        {i.email}
                      </td>
                      <td className="px-3 py-2 text-xs text-text-secondary">
                        {i.canales_presentes.map(etiquetaCanal).join(' · ')}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium text-state-danger">
                        {i.canales_faltantes.map(etiquetaCanal).join(' · ')}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {!i.acceso_id ? (
                          <span className="text-xs text-text-muted">
                            {MOTIVO_SIN_ACCION[i.motivo_sin_accion ?? 'sin_ticket']}
                          </span>
                        ) : puedeOperar && onCompletar ? (
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={ejecutando}
                            onClick={() => onCompletar(i)}
                          >
                            {enCurso === i.acceso_id
                              ? 'Completando…'
                              : `Completar en ${i.canales_faltantes
                                  .map(etiquetaCanal)
                                  .join(' y ')}`}
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Seccion>
          <Seccion
            titulo="Sin ticket de Acceso WiFi en el portal"
            descripcion="Tienen acceso en eWeLink pero el portal no lo registra, así que no puede suspenderlo por morosidad ni darlo de baja: queda fuera de todo control. Corresponde eliminarlos de eWeLink; si necesitan acceso, deben solicitarlo por el portal."
            cantidad={datos.huerfanos.length}
            seleccionadas={selHuerfanos.sel.size}
            accion="Eliminar de eWeLink"
            onAccionMasiva={() =>
              onEliminarDeEwelink?.(
                datos.huerfanos.filter((h) => selHuerfanos.marcada(h.email)),
              )
            }
            onLimpiar={selHuerfanos.limpiar}
            puedeOperar={puedeOperar}
            peligro
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-text-muted border-y border-line">
                    {puedeOperar && onEliminarDeEwelink && (
                      <th className="pl-4 pr-1 py-2 w-8">
                        <input
                          type="checkbox"
                          aria-label="Seleccionar todos"
                          checked={selHuerfanos.todas}
                          onChange={(e) =>
                            selHuerfanos.toggleTodas(e.target.checked)
                          }
                        />
                      </th>
                    )}
                    <th className="px-4 py-2 font-medium">Correo</th>
                    <th className="px-3 py-2 font-medium">Etiqueta en eWeLink</th>
                    <th className="px-3 py-2 font-medium">¿Quién es?</th>
                    <th className="px-3 py-2 font-medium">Con acceso desde</th>
                    {puedeOperar && onEliminarDeEwelink && (
                      <th className="px-4 py-2 font-medium text-right" />
                    )}
                  </tr>
                </thead>
                <tbody>
                  {datos.huerfanos.map((h) => (
                    <tr
                      key={h.email}
                      className="border-b border-line/60 last:border-0 hover:bg-bg-page/40"
                    >
                      {puedeOperar && onEliminarDeEwelink && (
                        <td className="pl-4 pr-1 py-2">
                          <input
                            type="checkbox"
                            aria-label={`Seleccionar ${h.email}`}
                            checked={selHuerfanos.marcada(h.email)}
                            onChange={() => selHuerfanos.toggle(h.email)}
                          />
                        </td>
                      )}
                      <td className="px-4 py-2 font-mono text-xs text-text-primary break-all">
                        {h.email}
                      </td>
                      <td className="px-3 py-2 text-text-secondary">
                        {h.comment ?? '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {h.lote_identificador ? (
                          <span className="text-text-primary">
                            Casa {h.lote_identificador}
                            {h.nombre_padron && (
                              <span className="text-text-muted">
                                {' '}
                                · {h.nombre_padron}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-text-muted">Sin identificar</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-text-muted whitespace-nowrap">
                        {h.compartido_desde ?? '—'}
                      </td>
                      {puedeOperar && onEliminarDeEwelink && (
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => onEliminarDeEwelink?.([h])}
                          >
                            Eliminar de eWeLink
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Seccion>
          <Seccion
            titulo="Registrados en el portal, sin acceso real"
            descripcion="Se dieron de alta, pero ya no están en ningún canal de eWeLink: alguien los quitó por fuera del portal y el vecino cree tener acceso. Volver a compartir los repone en todos los canales con su misma etiqueta. Los que figuran en validación tienen acceso sin autorización de la Comisión: se resuelven al validarlos o rechazarlos."
            cantidad={datos.sinAccesoReal.length}
            seleccionadas={selSinAcceso.sel.size}
            accion="Volver a compartir"
            onAccionMasiva={() =>
              onVolverACompartir?.(
                datos.sinAccesoReal.filter(
                  (f) =>
                    f.estado === 'acceso_perdido' &&
                    selSinAcceso.marcada(f.acceso_id),
                ),
              )
            }
            onLimpiar={selSinAcceso.limpiar}
            puedeOperar={puedeOperar}
          >
            <Tabla
              filas={datos.sinAccesoReal}
              accion="Volver a compartir"
              onEjecutar={(f) => onVolverACompartir?.([f])}
              filaOperable={(f) => f.estado === 'acceso_perdido'}
              puedeOperar={puedeOperar}
              seleccion={selSinAcceso}
              enCurso={enCurso}
              ejecutando={ejecutando}
              gerundio="Compartiendo…"
            />
          </Seccion>
        </div>
      )}
    </div>
  );
}
